<template>
  <!-- Content Detail Card -->
  <div
    class="bg-white p-[16px] rounded-[16px]"
    data-testid="testid-content-detail-card"
  >
    <!-- Content Image -->
    <div
      v-if="contentType === 1"
      class="flex justify-center mb-[16px]"
      data-testid="testid-content-video-wrapper"
    >
      <VideoCDN :jw-link="jwLink" />
    </div>

    <div v-else class="mb-[16px]" data-testid="testid-content-image-wrapper">
      <ImageCarousel :images="galleryImageUrls" />
    </div>
    <!-- Display Title -->
    <h1
      class="text-[14px] mb-[8px] leading-[20px] font-[500] text-[#292828] md:text-[16px] md:leading-[24px]"
      data-testid="testid-title-th"
    >
      {{ titleTh }}
    </h1>

    <!-- Display Publish Date -->
    <p
      class="mb-[16px] text-[12px] leading-[16px] font-[400] text-[#858282] md:text-[14px] md:leading-[20px]"
      data-testid="testid-publish-date"
    >
      {{ publishDate }}
    </p>

    <!-- Copy Link Button -->
    <div class="mb-[16px]" data-testid="testid-button-section">
      <button
        class="w-full flex flex-col justify-center items-center rounded-[16px] p-[8px] bg-[#F7F7F7] cursor-pointer"
        data-testid="testid-copy-link-btn"
        aria-label="Copy link"
        @click="onPressCopyLink"
        @mouseenter="() => EnterHover(true)"
        @mouseleave="() => LeaverHover(true)"
        @touchstart="() => EnterHover(false)"
        @touchend="() => LeaverHover(false)"
      >
        <div
          :class="`w-full h-full rounded-[8px] flex flex-col items-center gap-[4px] py-[8px] hover:bg-[#F0EFEF] ${pressCopied ? 'bg-[#FDE6E6] hover:bg-[#FDE6E6]' : isHovered ? 'bg-[#F7F7F7] hover:bg-[#F0EFEF] active:bg-[#F0EFEF] focus:bg-[#F0EFEF]' : 'bg-[#F7F7F7]'}`"
        >
          <img
            :src="pressCopied ? pressCopiedIcon : nonPressedIcon"
            alt="Copy Link"
            :class="`mb-[4px] w-[24px] h-[24px] md:w-[32px] md:h-[32px] pointer-events-none border rounded-[6px] aspect-[1/1] ${pressCopied || !isHovered ? 'border-[#E0E0E0]' : 'border-[#F0EFEF]'}`"
            data-testid="testid-copy-link-icon"
          />
          <span
            class="text-xs font-[500] md:text-[12px] md:leading-[16px] text-[#292828] select-text"
            data-testid="testid-copy-link-text"
          >
            {{ pressCopied ? "คัดลอกแล้ว" : "คัดลอกลิงก์" }}
          </span>
        </div>
      </button>
    </div>

    <!-- Reaction Section -->
    <ContentDetailReaction :content-id="contentId" :is-loading="false" />

    <!-- Display Description with Read More Toggle -->
    <div data-testid="testid-description-section">
      <p
        class="text-[14px] leading-[20px] font-[400] text-[#292828] whitespace-pre-wrap"
        data-testid="testid-description"
      >
        {{ descriptionThDisplay }}
      </p>
      <span
        v-if="!showReadMore"
        class="text-[14px] leading-[20px] font-[500] text-[#F06666] cursor-pointer hover:text-[#E60000]"
        data-testid="testid-read-more"
        @click="toggleReadMore"
      >
        ...อ่านเพิ่มเติม
      </span>
    </div>
  </div>
</template>

<script setup lang="ts">
import copiedIconDesktop from "@/assets/images/icon-copied-desktop.png";
import copiedIcon from "@/assets/images/icon-copied.png";
import copyIconDesktop from "@/assets/images/icon-copy-link-desktop.png";
import copyIcon from "@/assets/images/icon-copy-link.png";
const props = defineProps({
  contentId: {
    type: String,
    default: "",
  },
  galleryImageUrls: {
    type: Array as () => string[],
    default: () => [],
  },
  titleTh: {
    type: String,
    default: "",
  },
  publishDate: {
    type: String,
    default: "",
  },
  descriptionTh: {
    type: String,
    default: "",
  },
  contentType: {
    type: Number,
    default: 0,
  },
  jwLink: {
    type: String,
    default: "",
  },
});

const showReadMore = ref(props.contentType == 1);
const pressCopied = ref<boolean>(false);
const isHovered = ref<boolean>(false);
const isMobile = ref<boolean>(false);
const pressCopiedIcon = computed(() =>
  isMobile.value ? copiedIcon : copiedIconDesktop,
);
const nonPressedIcon = computed(() =>
  isMobile.value ? copyIcon : copyIconDesktop,
);

// Change this to function
const descriptionThDisplay = computed(() =>
  showReadMore.value
    ? props.descriptionTh
    : props.descriptionTh.slice(0, Math.ceil(props.descriptionTh.length * 0.3)),
);

const EnterHover = (isMouse: boolean) => {
  if ((isMouse && !isMobile.value) || (!isMouse && isMobile.value)) {
    isHovered.value = true;
  }
};

const LeaverHover = (isMouse: boolean) => {
  if ((isMouse && !isMobile.value) || (!isMouse && isMobile.value)) {
    isHovered.value = false;
  }
};

const onPressCopyLink = () => {
  if (pressCopied.value === false) {
    pressCopied.value = true;
    navigator.clipboard.writeText(window.location.href);
    setTimeout(() => {
      pressCopied.value = false;
    }, 3000);
  }
};

const toggleReadMore = () => {
  showReadMore.value = !showReadMore.value;
};

const checkMobile = () => {
  isMobile.value = window.innerWidth < 768;
};

onMounted(() => {
  window.addEventListener("resize", checkMobile);
});

onUnmounted(() => {
  window.removeEventListener("resize", checkMobile);
});
</script>
